import {
  BatchSearch,
  Icon,
  Info,
  Input,
  Multiselect,
  OptionProps,
  SlimButton,
  Toggle
} from 'components'
import { splittingString } from '../../NDPage'
import { useEffect, useState } from 'react'
import * as S from './styles'
import { Controller, useForm } from 'react-hook-form'
import { NDParamsType } from 'commons'
import useParams from 'commons/customHooks/useParams'
import { SaleRegion } from 'services/salesOrder/types'
import { useDispatch } from 'store/store'
import { setSortFilter } from 'store/NegotiationsContext/negotiationsSlice'

type FilterProps = {
  onSubmit: (data: NDParamsType) => void
  getSearchParams: () => NDParamsType
  optionsMesCota: string[]
  optionsOffices: string[]
  optionsOvTypes: string[]
  optionsSalesTeam: OptionProps[]
  optionsSalesRegion: SaleRegion[]
  defaultValues: NDParamsType
  ovTypes: string[]
}

export const Filter = ({
  onSubmit,
  getSearchParams,
  optionsMesCota,
  optionsOffices,
  optionsOvTypes,
  optionsSalesRegion,
  optionsSalesTeam,
  defaultValues,
  ovTypes
}: FilterProps) => {
  const dispatch = useDispatch()
  const [filterExpanded, setFilterExpanded] = useState(false)
  const [disabledHierarquia, setDisabledHierarquia] = useState(true)
  const { createParamsByObject, clearParamsExcept } = useParams()

  const disabledsButtons = [] as string[]

  const { control, reset, getValues, setValue, handleSubmit, watch } = useForm({
    defaultValues: getSearchParams()
  })
  const codSapClie = watch('codigosSapCliente')
  const OVNumbers = watch('numerosOrdemVenda')

  useEffect(() => {
    setFilterExpanded(getSearchParams().filterExpanded === 'true')
  }, [])

  useEffect(() => {
    if (codSapClie !== '') setDisabledHierarquia(false)
    else {
      setDisabledHierarquia(true)
      setValue('hierarquiaCliente', 'false')
    }
  }, [codSapClie])

  useEffect(() => {
    reset(getSearchParams())
  }, [optionsMesCota])

  useEffect(() => {
    if (OVNumbers !== '') {
      reset({ ...defaultValues, numerosOrdemVenda: OVNumbers })
    }
  }, [OVNumbers])

  const handleClearFilter = () => {
    clearParamsExcept(['filterExpanded'])
    reset(defaultValues)
    dispatch(setSortFilter({}))
  }

  const labelField = (label: string, name: string, disableLabel?: boolean) => (
    <S.LabelFilter disabled={disabledsButtons.includes(name) || disableLabel}>
      {label}
      {disabledsButtons.includes(name) && (
        <Info>Campo indisponível. Em breve mais informações.</Info>
      )}
    </S.LabelFilter>
  )

  const handleFilterExpand = () => {
    setFilterExpanded((oldValue) => {
      const newValue = !oldValue
      createParamsByObject({ ...getSearchParams(), filterExpanded: newValue.toString() })
      return newValue
    })
  }

  const filteredOptionsSalesRegion = () => {
    const codEquipe = watch('codigosEquipeVenda')
    if (codEquipe) {
      const cdEquipe = codEquipe.split(',')?.map((item) => item.substring(0, 3))

      return optionsSalesRegion
        .filter((item) =>
          item?.cd_equi_vend?.split(',').some((item) => cdEquipe.includes(item))
        )
        ?.map((region) => ({
          value: region.cd_regi_vend,
          title: region.ds_regi_vend
        }))
    }

    return optionsSalesRegion.map((region) => ({
      value: region.cd_regi_vend,
      title: region.ds_regi_vend
    }))
  }

  const formattingData = (data: NDParamsType) => {
    createParamsByObject({ ...data, filterExpanded: filterExpanded.toString() })
    onSubmit(data)
  }

  useEffect(() => {
    setValue('tiposOrdemVenda', ovTypes.join(','))
  }, [ovTypes])

  return (
    <S.CardFilter>
      <S.Form onSubmit={handleSubmit(formattingData)}>
        <S.AccordionFilter expanded={filterExpanded} onChange={handleFilterExpand}>
          <S.FormHeader>
            <S.WrapFormTitle>
              <S.FormTitle>NDs pendentes</S.FormTitle>
              <S.FormActions>
                <S.AccordionButton>
                  <S.TextAccordionButton>Filtros</S.TextAccordionButton>
                </S.AccordionButton>
              </S.FormActions>
            </S.WrapFormTitle>
          </S.FormHeader>
          <S.FormContent>
            <S.AccordionContent>
              <S.FormRow>
                <S.FormColumn>
                  {labelField('Equipe de vendas', 'codigosEquipeVenda')}
                  <Controller
                    name="codigosEquipeVenda"
                    control={control}
                    render={({ field }) => (
                      <Multiselect
                        {...field}
                        selectedOptions={
                          typeof field.value !== 'string'
                            ? field.value
                            : splittingString(field.value)
                        }
                        onChange={(value) => {
                          field.onChange(value.join())
                          const salesRegion = getValues('codigosRegiaoVenda')?.split(',')
                          const newRegion = salesRegion?.filter((item) =>
                            filteredOptionsSalesRegion()?.some(
                              (region) => region.title === item
                            )
                          )

                          setValue('codigosRegiaoVenda', newRegion.join())
                        }}
                        optionsList={optionsSalesTeam?.map((item) => item.title)}
                      />
                    )}
                  />
                </S.FormColumn>
                <S.FormColumn>
                  {labelField('Escritório', 'escritoriosVenda')}
                  <Controller
                    name="escritoriosVenda"
                    control={control}
                    render={({ field }) => (
                      <Multiselect
                        {...field}
                        selectedOptions={
                          typeof field.value !== 'string'
                            ? field.value
                            : splittingString(field.value)
                        }
                        onChange={(value) => field.onChange(value.join())}
                        optionsList={optionsOffices}
                      />
                    )}
                  />
                </S.FormColumn>
                <S.FormColumn>
                  {labelField('Região de vendas', 'codigosRegiaoVenda')}
                  <Controller
                    name="codigosRegiaoVenda"
                    control={control}
                    render={({ field }) => (
                      <Multiselect
                        {...field}
                        selectedOptions={
                          typeof field.value !== 'string'
                            ? field.value
                            : splittingString(field.value)
                        }
                        onChange={(value) => field.onChange(value.join())}
                        optionsList={filteredOptionsSalesRegion()?.map(
                          (item) => item.title
                        )}
                      />
                    )}
                  />
                </S.FormColumn>
                <S.FormColumn>
                  <Controller
                    name="codigosSapCliente"
                    control={control}
                    render={({ field }) => (
                      <BatchSearch
                        {...field}
                        label="Código SAP"
                        onClear={() => reset({ ...getValues(), codigosSapCliente: '' })}
                      />
                    )}
                  />
                </S.FormColumn>
                <S.FormColumn>
                  <Controller
                    name="razoesSociaisCliente"
                    control={control}
                    render={({ field }) => (
                      <BatchSearch
                        label="Razão Social"
                        onClear={() =>
                          reset({ ...getValues(), razoesSociaisCliente: '' })
                        }
                        {...field}
                      />
                    )}
                  />
                </S.FormColumn>
                <S.FormColumn>
                  <Controller
                    name="numerosOrdemVenda"
                    control={control}
                    render={({ field }) => (
                      <BatchSearch
                        label="OV portal"
                        onClear={() => reset({ ...getValues(), numerosOrdemVenda: '' })}
                        {...field}
                      />
                    )}
                  />
                </S.FormColumn>
              </S.FormRow>
              <S.FormRow>
                <S.FormColumn>
                  {labelField('Material', 'material')}
                  <Controller
                    name="material"
                    control={control}
                    render={({ field }) => <Input {...field} />}
                  />
                </S.FormColumn>
                <S.FormColumn>
                  {labelField('Tipo de material', 'tiposMaterial')}
                  <Controller
                    name="tiposMaterial"
                    control={control}
                    render={({ field }) => (
                      <Multiselect
                        {...field}
                        selectedOptions={
                          typeof field.value !== 'string'
                            ? field.value
                            : splittingString(field.value)
                        }
                        onChange={(value) => field.onChange(value.join())}
                        optionsList={['Fora de linha', 'Em linha']}
                      />
                    )}
                  />
                </S.FormColumn>
                <S.FormColumn>
                  {labelField('Tipo de OV', 'tiposOrdemVenda')}
                  <Controller
                    name="tiposOrdemVenda"
                    control={control}
                    render={({ field }) => (
                      <Multiselect
                        {...field}
                        selectedOptions={
                          typeof field.value !== 'string'
                            ? field.value
                            : splittingString(field.value)
                        }
                        onChange={(value) => field.onChange(value.join(','))}
                        optionsList={optionsOvTypes}
                      />
                    )}
                  />
                </S.FormColumn>
                <S.FormColumn>
                  {labelField('Mês cota', 'mesesCota')}
                  <Controller
                    name="mesesCota"
                    control={control}
                    render={({ field }) => (
                      <Multiselect
                        {...field}
                        selectedOptions={
                          typeof field.value !== 'string'
                            ? field.value
                            : splittingString(field.value)
                        }
                        onChange={(value) => {
                          field.onChange(value.join(','))
                        }}
                        optionsList={optionsMesCota}
                      />
                    )}
                  />
                </S.FormColumn>
                <S.FormColumn>
                  {labelField('Causa', 'causas')}
                  <Controller
                    name="causas"
                    control={control}
                    render={({ field }) => (
                      <Multiselect
                        {...field}
                        selectedOptions={
                          typeof field.value !== 'string'
                            ? field.value
                            : splittingString(field.value)
                        }
                        onChange={(value) => field.onChange(value.join())}
                        optionsList={[
                          'Prazo',
                          'Desconto',
                          'Prazo + Desconto',
                          'Bonificação'
                        ]}
                      />
                    )}
                  />
                </S.FormColumn>
                <S.FormColumn>
                  {labelField('Aprovação', 'statusAprovacao')}
                  <Controller
                    name="statusAprovacao"
                    control={control}
                    render={({ field }) => (
                      <Multiselect
                        {...field}
                        selectedOptions={
                          typeof field.value !== 'string'
                            ? field.value
                            : splittingString(field.value)
                        }
                        onChange={(value) => field.onChange(value.join())}
                        optionsList={['Aprovado', 'Não aprovado']}
                      />
                    )}
                  />
                </S.FormColumn>
              </S.FormRow>
              <S.FormRow className="flexEnd">
                <S.SubColumn>
                  {labelField('Hierarquia', 'hierarquiaCliente')}
                  <S.WrapToggleHierarchy>
                    <Controller
                      name="hierarquiaCliente"
                      control={control}
                      render={({ field }) => (
                        <Toggle
                          {...field}
                          disabled={disabledHierarquia}
                          setValue={(value) => field.onChange(value.toString())}
                          processChange={field.onBlur}
                          value={field.value === 'true'}
                        />
                      )}
                    />
                  </S.WrapToggleHierarchy>
                </S.SubColumn>
                <S.FormColumn className="lineColumn">
                  <SlimButton
                    id="filters-clear-button"
                    variant="secondary"
                    onClick={handleClearFilter}
                    type="button"
                    icon={<Icon icon="clear-all" size="20" />}
                  />
                  <SlimButton type="submit" variant="primary" content="Buscar" />
                </S.FormColumn>
              </S.FormRow>
            </S.AccordionContent>
          </S.FormContent>
        </S.AccordionFilter>
      </S.Form>
    </S.CardFilter>
  )
}
